import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';
import { CalendarServices } from '../services/calendar.services'
import buildItem from '@/core/services/calendar.util';

const CALENDAR_STORE_NAME = 'CalendarStore';
const CALENDAR_EVENT_TYPE_SCHOOL_CALENDAR = 'SchoolCalendar';
const CALENDAR_EVENT_TYPE_HOLYDAY = 'Holiday';
const CALENDAR_EVENT_TYPE_DAYOFF = 'DayOff';
const CALENDAR_EVENT_TYPE_EVENT = 'Event';
const CALENDAR_EVENT_TYPE_CLASS_SCHEDULE = 'ClassSchedule';

const CALENDAR_STORE_MUTATIONS = {
  SET_CALENDARS: 'setCalendars',
  SET_ITEMS: 'setItems',
  ADD_ITEMS: 'addItems',
  SET_ITEM: 'setItem',
  SET_ENABLE_CALENDARS: 'setEnableCalendars',
}

const CALENDAR_STORE_ACTIONS = {
  GET_CALENDARS : 'getCalendars',
  GET_ITEMS : 'getItems',
  REMOVE_ITEM : 'removeItem',
  REMOVE_ITEMS : 'removeItems',
  UPDATE_ITEM: 'updateItem',
  CREATE_ITEM: 'createItem',
  CLEAN_ITEMS: 'cleanItems'
}

export const CALENDAR_STORE = {
  NAME: CALENDAR_STORE_NAME,
  MUTATIONS:{
    SET_ENABLE_CALENDARS: `${CALENDAR_STORE_NAME}/${CALENDAR_STORE_MUTATIONS.SET_ENABLE_CALENDARS}`,
  } ,
  ACTIONS: {
    GET_CALENDARS: `${CALENDAR_STORE_NAME}/${CALENDAR_STORE_ACTIONS.GET_CALENDARS}`,
    GET_ITEMS: `${CALENDAR_STORE_NAME}/${CALENDAR_STORE_ACTIONS.GET_ITEMS}`,
    CREATE_ITEM: `${CALENDAR_STORE_NAME}/${CALENDAR_STORE_ACTIONS.CREATE_ITEM}`,
    UPDATE_ITEM: `${CALENDAR_STORE_NAME}/${CALENDAR_STORE_ACTIONS.UPDATE_ITEM}`,
    REMOVE_ITEM: `${CALENDAR_STORE_NAME}/${CALENDAR_STORE_ACTIONS.REMOVE_ITEM}`,
    REMOVE_ITEMS: `${CALENDAR_STORE_NAME}/${CALENDAR_STORE_ACTIONS.REMOVE_ITEMS}`,
    CLEAN_ITEMS: `${CALENDAR_STORE_NAME}/${CALENDAR_STORE_ACTIONS.CLEAN_ITEMS}`,
  }
}

@Module({ namespaced: true })
export class CalendarStore extends VuexModule {

  /* State */
  public calendars: any = {};

  //-- Items: tasks & events
  public items: any = [];

  //---------------------------------------------------------------------------
  //-- Mutations
  @Mutation
  public setCalendars(data: any) {
    this.calendars = data;
  }

  @Mutation
  public setEnableCalendars(data: number[]) {
    this.calendars.forEach(c=>{
      c.enable = false
    } );
  }

  @Mutation
  public setItems(data: any) {
    this.items =  data;
  }

  @Mutation
  public addItems(data: any) {
    this.items.push(...data);
  }

  @Mutation
  public setItem(data: any) {
    this.items.push(data);
  }

  //---------------------------------------------------------------------------
  //-- Actions

  
  @Action
  public async cleanItems() {
    this.context.commit(CALENDAR_STORE_MUTATIONS.SET_ITEMS, []);
  }

  @Action
  public async getCalendars() {
    let data: any = await CalendarServices.getCalendars(false);
    this.context.commit(CALENDAR_STORE_MUTATIONS.SET_CALENDARS, data);
  }

  @Action
  public async getItems(params: any) {
    const calendarEvents: any = await CalendarServices.getEvents(params);
    const classScheduleItems: any = await CalendarServices.getClassScheduleItems(params);

    const allItems = [];
    
    // Process calendar events
    calendarEvents.map(cal => {
      const events = cal.events.map(e => {
        if (e.customProperties['X-EDUAC-EVENT-TYPE'] === CALENDAR_EVENT_TYPE_SCHOOL_CALENDAR) {
          return [
            buildItem({ ...e, uid: e.uid + '_1', startDate: e.startDate, endDate: e.startDate, allDay: true, summary: `${e.summary} (Start)` }, cal.info.calendarId, false),
            buildItem({ ...e, uid: e.uid + '_2', startDate: e.endDate, endDate: e.endDate, allDay: true, summary: `${e.summary} (End)` }, cal.info.calendarId, false)
          ];
        } else {
          const canEdit = ![CALENDAR_EVENT_TYPE_DAYOFF, CALENDAR_EVENT_TYPE_HOLYDAY, CALENDAR_EVENT_TYPE_EVENT].includes(e.customProperties['X-EDUAC-EVENT-TYPE']);
          return buildItem(e, cal.info.calendarId, canEdit ?? cal.info.canEdit);
        }
      }).flat();
      allItems.push(...events);
    });

    // Process class schedule items
    if (classScheduleItems && classScheduleItems.calendars) {
      classScheduleItems.calendars.map(cal => {
        const events = cal.events.map(e => {
          if (e.customProperties['x_EDUAC_EVENT_TYPE'] === CALENDAR_EVENT_TYPE_CLASS_SCHEDULE) {
            return buildItem(e, cal.info.calendarId, false);
          }
        }).filter(Boolean); // Remove any undefined/null items
        allItems.push(...events);
      });
    }
    this.context.commit(CALENDAR_STORE_MUTATIONS.SET_ITEMS, allItems);
  }

  @Action
  public async removeItems(calId: string) {
    let data = this.items.filter( c => c.calId != calId)
    this.context.commit(CALENDAR_STORE_MUTATIONS.SET_ITEMS, data);
  }

  @Action
  public async createItem(data: any) {
    let response: any = await CalendarServices.createEventOrTask(data, data.calId, data.type);

    //-- Remove calendar items for the specific calendar
    let itemsCal = this.items.filter( c => c.calId != data.calId);
    this.context.commit(CALENDAR_STORE_MUTATIONS.SET_ITEMS, itemsCal);

    //-- Query the calendar events for the specific calendar
    let params = {start: data.calStartDate, end: data.calEndDate, calendars: [data.calId]}

    //-- call Action 'getItems' from current action
    await this.context.dispatch('getItems', params);
  }

  @Action
  public async updateItem(data: any) {
    //-- Update item
    let response = await CalendarServices.updateEventOrTask(data, data.calId, data.uid, data.type);

    //-- Remove calendar items for the specific calendar
    let itemsCal = this.items.filter(c => c.calId != data.calId);
    this.context.commit(CALENDAR_STORE_MUTATIONS.SET_ITEMS, itemsCal);

    //-- Query the calendar events for the specific calendar
    let params = {start: data.calStartDate, end: data.calEndDate, calendars: [data.calId]}

    //-- call Action 'getItems' from current action
    await this.context.dispatch('getItems', params);
  }

  @Action
  public async removeItem(payload: any) {
    let data = payload.item;
    await CalendarServices.removeEventOrTask(data.calId, data.id, data.type);

    //-- Remove calendar items for the specific calendar
    let itemsCal = this.items.filter(c => c.calId != data.calId);
    this.context.commit(CALENDAR_STORE_MUTATIONS.SET_ITEMS, itemsCal);

    //-- Query the calendar events for the specific calendar
    let params = {start: payload.calStartDate, end: payload.calEndDate, calendars: [data.calId]}

    //-- call Action 'getItems' from current action
    await this.context.dispatch('getItems', params);
  }

  /* Initial State */
  @Mutation
  public initState(state) {
    this.calendars = {};
    this.items = [];
  }
}
